import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../Buttons';
import {
  isChunkLoadError,
  markReload,
  reloadedRecently,
} from './chunkLoadError';

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  /** Clears the error when it changes (pass the current pathname). */
  resetKey?: string;
}

interface ChunkErrorBoundaryState {
  error: Error | null;
  resetKey?: string | undefined;
}

/**
 * Catches failures to download a code-split page so they don't blank the whole
 * app. Any other error is re-thrown, so it behaves exactly as before.
 */
export class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  override state: ChunkErrorBoundaryState = {
    error: null,
    resetKey: this.props.resetKey,
  };

  static getDerivedStateFromError(
    error: Error
  ): Partial<ChunkErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ChunkErrorBoundaryProps,
    state: ChunkErrorBoundaryState
  ): Partial<ChunkErrorBoundaryState> | null {
    // Navigating elsewhere clears the error so other pages render again
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  override componentDidCatch(error: Error, _info: ErrorInfo) {
    if (isChunkLoadError(error) && !reloadedRecently()) {
      markReload();
      window.location.reload();
    }
  }

  override render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    if (!isChunkLoadError(error)) {
      // Not ours to handle: propagate as if this boundary wasn't here
      throw error;
    }
    return (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-4 w-full h-full min-h-[50vh] text-center"
      >
        <p className="text-titleMedium text-on-surface-variant">
          Nie udało się załadować strony
        </p>
        <p className="text-bodyLarge text-on-surface-variant">
          Sprawdź połączenie z internetem i odśwież stronę.
        </p>
        <Button
          className="cursor-pointer"
          label="Odśwież"
          onClick={() => window.location.reload()}
        />
      </div>
    );
  }
}

export default ChunkErrorBoundary;
